interface StoreBindingIdentity {
  id: string;
}

const conversationShopSearchParameter = "shop";

/**
 * 对话范围只接受当前 validated seed；单店组织可确定性回落到唯一绑定。
 * seed 为空或已失效时，才使用首页已解析的 binding。市场代号不是店铺。
 * 多店且两边都没有绑定 id 时返回 null，调用方必须暂停请求，不能用空 scope 猜测。
 */
export function resolveConversationShopBindingId(
  selectedBindingId: string,
  bindings: ReadonlyArray<StoreBindingIdentity>,
  preferredBindingId?: string | null,
): string | null {
  if (bindings.some((binding) => binding.id === selectedBindingId)) {
    return selectedBindingId;
  }
  const preferred =
    typeof preferredBindingId === "string" ? preferredBindingId.trim() : "";
  if (preferred && bindings.some((binding) => binding.id === preferred)) {
    return preferred;
  }
  if (bindings.length === 0) return "";
  return bindings.length === 1 ? bindings[0]!.id : null;
}

/**
 * 对话 URL 中的 shop 只是服务端复核前的候选范围。显式但非法的候选必须
 * fail closed，不能静默回落到当前工作台偏好后读取另一家店的缓存内容。
 */
export function resolveConversationRouteShopBindingId(
  search: string,
  selectedBindingId: string,
  bindings: ReadonlyArray<StoreBindingIdentity>,
): string | null {
  const params = new URLSearchParams(search);
  const values = params.getAll(conversationShopSearchParameter);
  if (values.length > 1) return null;
  if (values.length === 1) {
    const candidate = values[0]?.trim() ?? "";
    if (!candidate || candidate.length > 200) return null;
    return bindings.some((binding) => binding.id === candidate)
      ? candidate
      : null;
  }
  return resolveConversationShopBindingId(selectedBindingId, bindings);
}

export function confirmConversationShopBindingId(
  search: string,
  routeCandidate: string | null,
  context: { selectionStatus: "selected" | "singleton" | "ambiguous" | "missing"; selectedBindingId: string | null } | null,
): string | null {
  if (!context || context.selectionStatus === "ambiguous") return null;
  if (context.selectionStatus === "selected" || context.selectionStatus === "singleton") {
    if (!context.selectedBindingId) return null;
    // 旧会话已有固定店铺时，显式 URL 候选不能覆盖后端归属；无 URL 时直接使用后端归属。
    return new URLSearchParams(search).has("shop") && routeCandidate !== context.selectedBindingId
      ? null : context.selectedBindingId;
  }
  return routeCandidate === "" ? "" : null;
}

export function firstConversationShopCandidate(
  ...candidates: Array<string | null | undefined>
): string {
  for (const candidate of candidates) {
    const value = candidate?.trim() ?? "";
    if (value) return value;
  }
  return "";
}

export function workspaceConversationPath(
  conversationId: string,
  shopBindingId: string,
  search = "",
) {
  const params = new URLSearchParams(search);
  params.delete(conversationShopSearchParameter);
  const canonicalShop = firstConversationShopCandidate(shopBindingId);
  if (canonicalShop) params.set(conversationShopSearchParameter, canonicalShop);
  const suffix = params.toString();
  const path = `/workspace/conversations/${encodeURIComponent(conversationId)}`;
  return suffix ? `${path}?${suffix}` : path;
}
